// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack — Audit Log Viewer
// Route: /audit
// apps/web/src/routes/audit.tsx
//
// Enterprise-gated audit trail viewer. Shows all system actions with
// user attribution, IP tracking, entity diffs, and correlation IDs.
// ═══════════════════════════════════════════════════════════════════════════

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '../components/engine/tokens';
import EnginePageShell, {
  GlassPanel,
  SectionLabel,
  MetricCard,
  EmptyState,
  LoadingShimmer,
  StatusBadge,
} from '../components/engine/EnginePageShell';
import { useCounter } from '../components/engine/hooks';
import { useSubscription, normalizeTier, meetsMinTier } from '../lib/api/billing';
import {
  useAuditLogs,
  useAuditStats,
  useAuditActions,
  type AuditLogEntry,
  type AuditFilters,
} from '../lib/api/audit';

export const Route = createFileRoute('/audit')({
  component: AuditPage,
});

// ─── Action color mapping ──────────────────────────────────────────────────

function actionColor(action: string): string {
  if (action.includes('Created') || action.includes('Added')) return C.neon;
  if (action.includes('Updated') || action.includes('Changed')) return C.amber;
  if (action.includes('Deleted') || action.includes('Removed')) return C.coral;
  if (action.includes('Login') || action.includes('Auth')) return C.violet;
  if (action.includes('Subscription') || action.includes('Billing')) return C.cyan;
  return C.blue;
}

// ─── Main Component ─────────────────────────────────────────────────────────

function AuditPage() {
  const { data: subscription } = useSubscription();
  const currentTier = normalizeTier(subscription?.tier);
  const hasAccess = meetsMinTier(currentTier, 'Enterprise');

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<AuditFilters>({});
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data: stats, loading: statsLoading } = useAuditStats();
  const { data: logsPage, loading: logsLoading } = useAuditLogs(page, filters);
  const { data: actions } = useAuditActions();

  const countTotal = useCounter(stats?.totalLogs ?? 0, 600, 200);
  const countToday = useCounter(stats?.todayCount ?? 0, 500, 300);
  const countUsers = useCounter(stats?.uniqueUsers ?? 0, 400, 400);

  return (
    <EnginePageShell
      title="Audit Log"
      subtitle="SYSTEM ACTIVITY"
      icon="&#x1f4cb;"
      accentColor={C.blue}
      statusLabel="ENTERPRISE"
      statusColor={C.amber}
    >
      {/* ── Tier gate ──────────────────────────────────────────────────── */}
      {!hasAccess ? (
        <GlassPanel accent={C.amber} delay={0.2} style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>&#x1f4cb;</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>
            Audit Log
          </div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.6, maxWidth: 500, margin: '0 auto 20px' }}>
            Complete activity trail with user attribution, IP tracking, entity change diffs,
            and correlation IDs for every action in your workspace.
          </div>
          <div style={{
            display: 'inline-block', fontSize: 10, fontFamily: F.mono, letterSpacing: '0.1em',
            color: C.amber, background: `${C.amber}15`, border: `1px solid ${C.amber}30`,
            borderRadius: 6, padding: '6px 16px',
          }}>
            REQUIRES ENTERPRISE PLAN
          </div>
        </GlassPanel>
      ) : (
        <>
          {/* ── Metrics ──────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
            <MetricCard label="Total Events" value={countTotal} icon="&#x1f4cb;" color={C.blue}
              tooltip={<div>Total audit log entries for this workspace.</div>} />
            <MetricCard label="Today" value={countToday} icon="&#x1f4c5;" color={C.neon}
              tooltip={<div>Events recorded today (UTC).</div>} />
            <MetricCard label="Unique Users" value={countUsers} icon="&#x1f465;" color={C.violet}
              tooltip={<div>Distinct users with audit trail activity.</div>} />
          </div>

          {/* ── Top actions breakdown ─────────────────────────────────── */}
          {stats && stats.byAction.length > 0 && (
            <GlassPanel accent={C.violet} delay={0.3} style={{ padding: 14, marginBottom: 20 }}>
              <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em', marginBottom: 8 }}>TOP ACTIONS</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {stats.byAction.slice(0, 8).map(a => (
                  <button
                    key={a.action}
                    onClick={() => { setFilters({ action: a.action }); setPage(1); }}
                    style={{
                      background: filters.action === a.action ? `${actionColor(a.action)}20` : `${C.dim}08`,
                      border: `1px solid ${filters.action === a.action ? `${actionColor(a.action)}40` : C.faint}`,
                      color: filters.action === a.action ? actionColor(a.action) : C.muted,
                      fontFamily: F.mono, fontSize: 10, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                    }}
                  >
                    {a.action} <span style={{ color: C.dim }}>({a.count})</span>
                  </button>
                ))}
              </div>
            </GlassPanel>
          )}

          {/* ── Filters ──────────────────────────────────────────────── */}
          <GlassPanel accent={C.blue} delay={0.35} style={{ padding: 12, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, letterSpacing: '0.1em' }}>FILTER</span>
              <select
                value={filters.action ?? ''}
                onChange={e => { setFilters(f => ({ ...f, action: e.target.value || undefined })); setPage(1); }}
                style={filterSelectStyle}
              >
                <option value="">All Actions</option>
                {actions?.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
              {(filters.action || filters.entityType) && (
                <button onClick={() => { setFilters({}); setPage(1); }} style={{
                  background: 'transparent', border: `1px solid ${C.faint}`, color: C.dim,
                  fontFamily: F.mono, fontSize: 9, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                }}>CLEAR</button>
              )}
            </div>
          </GlassPanel>

          {/* ── Log list ──────────────────────────────────────────────── */}
          <SectionLabel color={C.blue}>Activity Stream</SectionLabel>
          {logsLoading || statsLoading ? (
            <GlassPanel delay={0.4}><LoadingShimmer height={300} /></GlassPanel>
          ) : logsPage && logsPage.items.length > 0 ? (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {logsPage.items.map(entry => (
                  <LogRow
                    key={entry.id}
                    entry={entry}
                    expanded={expandedId === entry.id}
                    onToggle={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {logsPage.totalCount > logsPage.pageSize && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={pageBtnStyle(page > 1)}>PREV</button>
                  <span style={{ fontSize: 11, fontFamily: F.mono, color: C.muted, padding: '6px 12px' }}>
                    {page} / {Math.ceil(logsPage.totalCount / logsPage.pageSize)}
                  </span>
                  <button
                    disabled={page * logsPage.pageSize >= logsPage.totalCount}
                    onClick={() => setPage(p => p + 1)}
                    style={pageBtnStyle(page * logsPage.pageSize < logsPage.totalCount)}
                  >NEXT</button>
                </div>
              )}
            </>
          ) : (
            <GlassPanel accent={C.blue} delay={0.4}>
              <EmptyState icon="&#x1f4cb;" title="No audit log entries" subtitle="Activity will appear here as users interact with the workspace." />
            </GlassPanel>
          )}
        </>
      )}
    </EnginePageShell>
  );
}

// ─── LogRow ────────────────────────────────────────────────────────────────

function LogRow({ entry, expanded, onToggle }: {
  entry: AuditLogEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  const color = actionColor(entry.action);
  const time = new Date(entry.timestamp);

  return (
    <div style={{
      background: C.surface, border: `1px solid ${expanded ? `${color}30` : C.faint}`,
      borderRadius: 8, overflow: 'hidden',
    }}>
      <div onClick={onToggle} style={{
        display: 'grid', gridTemplateColumns: '110px 1fr 0.6fr 80px 60px',
        gap: 10, padding: '10px 14px', alignItems: 'center', cursor: 'pointer',
      }}>
        {/* Timestamp */}
        <div style={{ fontSize: 10, fontFamily: F.mono, color: C.dim }}>
          {time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{' '}
          {time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
        </div>

        {/* Action */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StatusBadge label={entry.action} color={color} />
        </div>

        {/* Entity */}
        <div style={{ fontSize: 11, fontFamily: F.mono, color: C.muted }}>
          {entry.entityType ? `${entry.entityType}#${entry.entityId ?? '?'}` : '—'}
        </div>

        {/* User */}
        <div style={{ fontSize: 10, fontFamily: F.mono, color: C.violet }}>
          {entry.userId ? `User #${entry.userId}` : 'System'}
        </div>

        {/* IP */}
        <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, textAlign: 'right' }}>
          {entry.ipAddress?.split(',')[0] || '—'}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{
          padding: '0 14px 14px', borderTop: `1px solid ${C.faint}`,
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, paddingTop: 14,
        }}>
          <div>
            <DetailLabel>CORRELATION ID</DetailLabel>
            <div style={{ fontSize: 10, fontFamily: F.mono, color: C.cyan, wordBreak: 'break-all' }}>
              {entry.correlationId || '—'}
            </div>

            <DetailLabel>IP ADDRESS</DetailLabel>
            <div style={{ fontSize: 10, fontFamily: F.mono, color: C.muted }}>{entry.ipAddress || '—'}</div>

            <DetailLabel>USER AGENT</DetailLabel>
            <div style={{ fontSize: 9, fontFamily: F.mono, color: C.dim, wordBreak: 'break-all', maxHeight: 60, overflow: 'auto' }}>
              {entry.userAgent || '—'}
            </div>
          </div>

          <div>
            {entry.oldValue && (
              <>
                <DetailLabel>PREVIOUS VALUE</DetailLabel>
                <pre style={jsonPreStyle}>{formatJson(entry.oldValue)}</pre>
              </>
            )}
            {entry.newValue && (
              <>
                <DetailLabel>NEW VALUE</DetailLabel>
                <pre style={jsonPreStyle}>{formatJson(entry.newValue)}</pre>
              </>
            )}
            {!entry.oldValue && !entry.newValue && (
              <div style={{ fontSize: 10, color: C.dim, fontStyle: 'italic' }}>No value snapshots recorded.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function DetailLabel({ children }: { children: string }) {
  return <div style={{ fontSize: 8, fontFamily: F.mono, color: C.dim, letterSpacing: '0.12em', marginBottom: 2, marginTop: 10 }}>{children}</div>;
}

function formatJson(value: string): string {
  try { return JSON.stringify(JSON.parse(value), null, 2); }
  catch { return value; }
}

const filterSelectStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.3)', border: `1px solid ${C.faint}`, borderRadius: 6,
  padding: '5px 10px', color: C.text, fontFamily: F.mono, fontSize: 10, outline: 'none',
};

const jsonPreStyle: React.CSSProperties = {
  fontSize: 10, fontFamily: F.mono, color: C.muted, background: 'rgba(0,0,0,0.3)',
  borderRadius: 6, padding: 10, overflow: 'auto', maxHeight: 150, margin: 0,
  whiteSpace: 'pre-wrap', wordBreak: 'break-all',
};

function pageBtnStyle(enabled: boolean): React.CSSProperties {
  return {
    background: enabled ? `${C.blue}15` : 'transparent',
    border: `1px solid ${enabled ? `${C.blue}30` : C.faint}`,
    color: enabled ? C.blue : C.dim,
    fontFamily: F.mono, fontSize: 10, padding: '6px 16px', borderRadius: 6,
    cursor: enabled ? 'pointer' : 'not-allowed',
  };
}
